import React, { useState, useRef } from "react";
import Navbar from "../navbar/Navbar";
import "../shared/starfield.css";

// ─── Per-persona walkthroughs ──────────────────────────────────────────────
// Each section is rendered by <Section /> below. `image` may point at an
// asset that doesn't exist yet — the <img> onError silently hides it.
const RESEARCHER_SECTIONS = [
  {
    id: "r-datasets",
    tag: "Data",
    title: "Datasets",
    image: "/assets/DV_datasets.png",
    body: (
      <>
        Upload and keep your datasets in a central place. Discovery Viewer
        organizes them by <strong>modality, anatomy, series type</strong>, and
        more — so the same cohort can be reused across every project. Combine
        your own data with open-source datasets that complement your cohorts,
        and govern exactly who you share with.
      </>
    ),
  },
  {
    id: "r-models",
    tag: "Intelligence",
    title: "AI Models & Tools",
    image: "/assets/DV_models.png",
    body: (
      <>
        Bring your own containerized AI models — or lightweight programmatic
        tools — to process your datasets and power interactive experiences for
        your reviewers. From foundation models to bespoke pipelines, everything
        runs alongside your data.
      </>
    ),
  },
  {
    id: "r-viewers",
    tag: "Interaction",
    title: "Custom Viewers",
    image: "/assets/DV_CustomViewers.png",
    body: (
      <>
        Design your own multi-modal viewers, or upload custom ones. Embed AI
        models for one-click inference, and drop in custom forms to collect
        structured feedback from your reviewers.
      </>
    ),
  },
  {
    id: "r-studies",
    tag: "Collaboration",
    title: "Studies",
    image: "/assets/DV_study.png",
    body: (
      <>
        Studies are the <strong>founding block</strong> of the platform.
        Combine datasets, AI models, tools, and custom viewers, then invite
        community members to review — this is where the collaboration actually
        happens.
      </>
    ),
  },
  {
    id: "r-projects",
    tag: "Scale",
    title: "Projects",
    image: "/assets/DV_projects.png",
    body: (
      <>
        Projects group related studies into a larger collaboration space with a
        shared team of members — your main workspace once research scales
        beyond a single study.
      </>
    ),
  },
];

const REVIEWER_SECTIONS = [
  {
    id: "rv-studies",
    tag: "Your work",
    title: "Go straight to your studies",
    image: "/assets/DV_ReviewerStudies.png",
    body: (
      <>
        As a reviewer, the studies assigned to you land here. Each one comes
        with instructions describing exactly what you're assessing — read them
        first, then open the study to start.
      </>
    ),
  },
  {
    id: "rv-annotate",
    tag: "Annotate",
    title: "Annotate & keep track of your progress",
    image: "/assets/DV_ReviewerAnnotate.png",
    body: (
      <>
        Work through each case inside the embedded viewer designed for the
        study. Discovery Viewer tracks your progress automatically, so you can
        pause and resume any time without losing where you were.
      </>
    ),
  },
  {
    id: "rv-open-datasets",
    tag: "Curious?",
    title: "Explore Open Datasets",
    image: "/assets/DV_OpenDatasets.png",
    body: (
      <>
        Browse open datasets shared by researchers around the world — and share
        your own annotations back, so the community benefits from your work.
      </>
    ),
  },
  {
    id: "rv-open-models",
    tag: "Curious?",
    title: "Try Open Medical Models",
    image: "/assets/DV_OpenModels.png",
    body: (
      <>
        Test community-shared AI models on your own cases, refine them where
        they fall short, and leave feedback that helps their creators improve.
      </>
    ),
  },
];

const OTHER_SECTIONS = [
  {
    id: "o-overview",
    tag: "Overview",
    title: "The platform in one glance",
    image: "/assets/LandingPage.png",
    body: (
      <>
        Discovery Viewer is a collaborative medical AI research platform.
        Researchers manage datasets, plug in AI models, and design custom
        viewers; reviewers annotate; everything is tied together through
        studies and projects.
      </>
    ),
  },
  {
    id: "o-datasets",
    tag: "Open source",
    title: "Open Datasets Hub",
    image: "/assets/DV_OpenDatasets.png",
    body: (
      <>
        Explore open-source medical datasets from around the world, cross-
        referenced with the classic sources (TCIA, Kaggle, Zenodo, and more).
        No login required.
      </>
    ),
  },
  {
    id: "o-models",
    tag: "Open source",
    title: "Open Medical Models",
    image: "/assets/DV_OpenModels.png",
    body: (
      <>
        Browse and read up on state-of-the-art medical AI — from
        TotalSegmentator and SAM 2 to Med-PaLM 2 and BioGPT — with links
        straight to the source papers.
      </>
    ),
  },
];

const SECTIONS_BY_ROLE = {
  researcher: RESEARCHER_SECTIONS,
  reviewer: REVIEWER_SECTIONS,
  other: OTHER_SECTIONS,
};

const ROLES = [
  { key: "researcher", label: "Researcher", icon: "fa-flask",           desc: "Build datasets, run studies, invite collaborators." },
  { key: "reviewer",   label: "Reviewer",   icon: "fa-pencil-square-o", desc: "Annotate the studies assigned to you." },
  { key: "other",      label: "Other",      icon: "fa-user",            desc: "General overview of the platform." },
];

// ─── Sub-components ────────────────────────────────────────────────────────
const Section = ({ section, idx }) => {
  const flipped = idx % 2 === 1;
  return (
    <section id={section.id} style={{
      display: "flex",
      flexDirection: flipped ? "row-reverse" : "row",
      alignItems: "center",
      justifyContent: "center",
      gap: "clamp(24px, 4vw, 72px)",
      minHeight: "80vh",
      padding: "clamp(40px, 8vh, 96px) clamp(20px, 5vw, 80px)",
      flexWrap: "wrap",
      position: "relative",
      zIndex: 1,
    }}>
      <div style={{ flex: "1 1 460px", maxWidth: "640px", display: "flex", justifyContent: "center" }}>
        <img
          src={section.image}
          alt={section.title}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          style={{
            width: "100%",
            maxHeight: "520px",
            objectFit: "contain",
            borderRadius: "14px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
          }}
        />
      </div>
      <div style={{ flex: "1 1 380px", maxWidth: "520px" }}>
        <div style={{
          display: "inline-block",
          padding: "4px 12px",
          borderRadius: "999px",
          background: "rgba(56,182,255,0.12)",
          border: "1px solid rgba(56,182,255,0.35)",
          color: "#38b6ff",
          fontFamily: "Poppins, sans-serif",
          fontSize: "11px",
          fontWeight: 700,
          letterSpacing: "1.2px",
          textTransform: "uppercase",
          marginBottom: "16px",
        }}>
          0{idx + 1} · {section.tag}
        </div>
        <h2 style={{
          margin: "0 0 18px",
          fontFamily: "Poppins, sans-serif",
          fontSize: "clamp(28px, 4vw, 48px)",
          fontWeight: 700,
          lineHeight: 1.1,
          color: "#ffffff",
        }}>
          {section.title}
        </h2>
        <p style={{
          margin: 0,
          fontFamily: "Poppins, sans-serif",
          fontSize: "clamp(15px, 1.4vw, 18px)",
          lineHeight: 1.7,
          color: "#c0cbdb",
        }}>
          {section.body}
        </p>
      </div>
    </section>
  );
};

const RoleTab = ({ role, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      flex: "1 1 220px",
      maxWidth: "300px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: "10px",
      padding: "24px 20px",
      borderRadius: "16px",
      cursor: "pointer",
      background: active ? "rgba(56,182,255,0.14)" : "rgba(15,20,39,0.6)",
      border: active ? "2px solid #38b6ff" : "2px solid rgba(255,255,255,0.08)",
      color: active ? "#ffffff" : "#c0cbdb",
      fontFamily: "Poppins, sans-serif",
      transition: "all 0.2s cubic-bezier(0.2, 0.9, 0.3, 1)",
      transform: active ? "translateY(-2px)" : "translateY(0)",
      boxShadow: active ? "0 10px 32px rgba(56,182,255,0.25)" : "none",
    }}
    onMouseEnter={(e) => {
      if (!active) e.currentTarget.style.borderColor = "rgba(56,182,255,0.35)";
    }}
    onMouseLeave={(e) => {
      if (!active) e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
    }}
  >
    <i className={`fa ${role.icon}`} style={{ fontSize: "28px", color: active ? "#38b6ff" : "#8a9ab5" }} />
    <span style={{ fontSize: "17px", fontWeight: 700, color: "#ffffff" }}>{role.label}</span>
    <span style={{ fontSize: "13px", color: "#8a9ab5", lineHeight: 1.4 }}>{role.desc}</span>
  </button>
);

// ─── Main component ────────────────────────────────────────────────────────
const LandingPage = () => {
  const [role, setRole] = useState(null);
  const rolePickerRef = useRef(null);
  const firstSectionAnchorRef = useRef(null);

  const scrollToRolePicker = () => {
    rolePickerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSelectRole = (key) => {
    setRole(key);
    // Give React a tick to render the new sections, then smooth-scroll into
    // the first one so the user sees the walkthrough start.
    window.setTimeout(() => {
      firstSectionAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const currentSections = role ? SECTIONS_BY_ROLE[role] : [];

  return (
    <div style={{ minHeight: "100vh", color: "#ffffff", display: "flex", flexDirection: "column" }}>

      {/* Static starfield background */}
      <div className="starfield-background" />

      {/* Subtle blue radial glow */}
      <div style={{
        position: "fixed", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0,
        background: "radial-gradient(ellipse 80% 60% at 50% 20%, rgba(56,182,255,0.07) 0%, transparent 70%)",
      }} />

      <Navbar />

      {/* Hero — sized to fill exactly one viewport minus the 76px navbar. */}
      <main style={{
        minHeight: "calc(100vh - 76px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "clamp(16px, 2.5vh, 28px)",
        padding: "clamp(16px, 3vh, 32px) 40px 96px",
        position: "relative",
        zIndex: 1,
      }}>
        {/* Badge */}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: "10px",
          background: "rgba(56,182,255,0.1)", border: "1px solid rgba(56,182,255,0.25)",
          borderRadius: "999px",
          padding: "clamp(5px, 0.8vw, 8px) clamp(14px, 1.8vw, 22px)",
          maxWidth: "94vw",
        }}>
          <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#38b6ff", display: "inline-block", boxShadow: "0 0 10px #38b6ff", flexShrink: 0 }} />
          <span style={{
            fontSize: "clamp(11px, 1.5vw, 18px)",
            fontFamily: "Poppins, sans-serif", color: "#38b6ff", fontWeight: "500", letterSpacing: "0.5px",
            textAlign: "center",
          }}>
            Mount Sinai - BMEII - Collaborative Medical AI Research Platform
          </span>
        </div>

        {/* Image + Title row */}
        <div style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: "clamp(20px, 4vw, 40px)",
          flexWrap: "wrap",
        }}>
          <img
            src="/assets/LandingPage.png"
            alt="Monitor with Discovery Viewer"
            style={{
              height: "clamp(200px, 38vh, 380px)",
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
          <h1 style={{
            margin: 0,
            lineHeight: 1.05,
            fontFamily: "Poppins, sans-serif",
            fontWeight: "bold",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}>
            <div style={{ fontSize: "clamp(56px, 10vh, 108px)" }}>
              <span style={{ fontSize: "clamp(66px, 12vh, 126px)", color: "#38b6ff", fontFamily: "Poppins, sans-serif" }}>D</span>
              iscovery
            </div>
            <div style={{ fontSize: "clamp(56px, 10vh, 108px)" }}>
              <span style={{ fontSize: "clamp(66px, 12vh, 126px)", color: "#38b6ff", fontFamily: "Poppins, sans-serif" }}>V</span>
              iewer
            </div>
          </h1>
        </div>

        {/* Scroll-down hint — the only CTA now; clicking it jumps to the role
            picker below the fold. Animated bounce draws the eye. */}
        <button
          onClick={scrollToRolePicker}
          aria-label="Scroll to platform overview"
          style={{
            position: "absolute",
            bottom: "24px",
            left: "50%",
            background: "none",
            border: "none",
            color: "rgba(255,255,255,0.75)",
            cursor: "pointer",
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            fontWeight: 500,
            letterSpacing: "1.4px",
            textTransform: "uppercase",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
            padding: "8px 14px",
            animation: "dv-bounce 1.8s ease-in-out infinite",
          }}
        >
          Scroll to explore
          <i className="fa fa-chevron-down" style={{ fontSize: "16px" }} />
        </button>
      </main>

      {/* Role picker — the first thing below the fold. */}
      <section
        ref={rolePickerRef}
        style={{
          padding: "clamp(60px, 12vh, 120px) clamp(20px, 5vw, 80px) clamp(40px, 8vh, 80px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "36px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ textAlign: "center", maxWidth: "640px" }}>
          <div style={{
            display: "inline-block",
            padding: "4px 12px",
            borderRadius: "999px",
            background: "rgba(56,182,255,0.12)",
            border: "1px solid rgba(56,182,255,0.35)",
            color: "#38b6ff",
            fontFamily: "Poppins, sans-serif",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "1.2px",
            textTransform: "uppercase",
            marginBottom: "16px",
          }}>
            How it works
          </div>
          <h2 style={{
            margin: "0 0 14px",
            fontFamily: "Poppins, sans-serif",
            fontSize: "clamp(28px, 4vw, 44px)",
            fontWeight: 700,
            lineHeight: 1.15,
            color: "#ffffff",
          }}>
            What best describes your role?
          </h2>
          <p style={{
            margin: 0,
            fontFamily: "Poppins, sans-serif",
            fontSize: "clamp(14px, 1.4vw, 17px)",
            color: "#8a9ab5",
            lineHeight: 1.6,
          }}>
            Pick the walkthrough that fits how you'll use Discovery Viewer.
          </p>
        </div>

        <div style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "20px",
          width: "100%",
          maxWidth: "1000px",
        }}>
          {ROLES.map(r => (
            <RoleTab key={r.key} role={r} active={role === r.key} onClick={() => handleSelectRole(r.key)} />
          ))}
        </div>
      </section>

      {/* Anchor placed just above the first section so the smooth-scroll on
          role select lands the persona walkthrough at the top of the view. */}
      <div ref={firstSectionAnchorRef} />

      {currentSections.map((s, i) => (
        <Section key={s.id} section={s} idx={i} />
      ))}

      {/* Scroll-hint bounce keyframes. Defined here to keep the landing page
          self-contained (no extra CSS file to import). */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes dv-bounce {
          0%, 100% { transform: translateX(-50%) translateY(0); opacity: 0.6; }
          50%      { transform: translateX(-50%) translateY(6px); opacity: 1; }
        }
      ` }} />
    </div>
  );
};

export default LandingPage;
